import type {
  BlockControlMethodHelpers,
  BlockStateDefinition,
} from '@unifyapps/carbon/no-code/types/blockDefinition';
import type { Action } from '@unifyapps/defs/types/action';
import type {
  BlockStateUnionType,
  BlockType,
  ComponentTypeUnionType,
} from '@unifyapps/defs/types/block';
import type { CardBlockStateType, CardComponentType } from '@unifyapps/defs/types/blocks';
import _pick from 'lodash/pick';
import { generatePreview } from '../utils/generatePreview';
import Card from './Card';
import type { CardRef } from './types';

// No need to lazy load Card, since it's used a lot and is a layout block
class CardBlockStateDefinition implements BlockStateDefinition {
  getBlockVersion() {
    return 0;
  }

  useBlockRef = true;

  get type(): ComponentTypeUnionType {
    return 'Card';
  }

  get initialStateGetter() {
    return (block: BlockType) => {
      const isCollapsed = !(
        (block.component as CardComponentType).content.collapsible?.isInitiallyExpanded ?? true
      );
      return {
        id: block.id,
        ..._pick(block.component as CardComponentType, ['content', 'componentType']),
        isCollapsed,
        appearance: {
          ...(block.component as CardComponentType).appearance,
          isCollapsed,
        },
      } as BlockStateUnionType;
    };
  }
  getBlockControlMethods() {
    return [
      {
        id: 'toggleCollapse' as const,
        label: 'Toggle Collapse State',
        method: (blockState: CardBlockStateType, _: Action, blockRef?: CardRef) => {
          if (blockRef) {
            blockRef.setOrToggleCard();
          } else {
            // If no blockRef, card is not mounted - update state directly
            return {
              ...blockState,
              isCollapsed: !blockState.isCollapsed,
              appearance: {
                ...blockState.appearance,
                isCollapsed: !blockState.appearance?.isCollapsed,
              },
            } as BlockStateUnionType;
          }
          return Promise.resolve();
        },
      },
      {
        id: 'collapse' as const,
        label: 'Collapse Card',
        method: (blockState: CardBlockStateType, _: Action, blockRef?: CardRef) => {
          if (blockRef) {
            blockRef.setOrToggleCard(false);
          } else {
            // If no blockRef, card is not mounted - update state directly
            return {
              ...blockState,
              isCollapsed: true,
              appearance: {
                ...blockState.appearance,
                isCollapsed: true,
              },
            } as BlockStateUnionType;
          }
          return Promise.resolve();
        },
      },
      {
        id: 'expand' as const,
        label: 'Expand Card',
        method: (blockState: CardBlockStateType, _: Action, blockRef?: CardRef) => {
          if (blockRef) {
            blockRef.setOrToggleCard(true);
          } else {
            // If no blockRef, card is not mounted - update state directly
            return {
              ...blockState,
              isCollapsed: false,
              appearance: {
                ...blockState.appearance,
                isCollapsed: false,
              },
            } as BlockStateUnionType;
          }
          return Promise.resolve();
        },
      },
      {
        id: 'generatePreview' as const,
        label: 'Generate Preview',
        method: async (
          blockState: BlockStateUnionType,
          _action: Action,
          _blockRef: unknown,
          helpers: BlockControlMethodHelpers,
        ) => {
          return await generatePreview(blockState, helpers);
        },
      },
    ];
  }

  getComponent() {
    return Card;
  }
}

export default CardBlockStateDefinition;
